import type { BundleSlot } from '@commerce-cart-webcomponents/sdk';
import MasterCountryList from '@com.amway.commerce/masterdata-country-v1-draft/data.json';
import { CountryInterface } from '@com.amway.commerce/masterdata-country-v1-draft/Schema';
import { Theme, TagStyle, IconStyle, AccountType, DynamicObjectInterface } from '@nextgen-web-framework/all';
import type { PaymentDetails } from '@commerce-payment-webcomponents/sdk';

import {
  DateFormats,
  DateOrTime,
  formatDateTime,
  TimeFormats,
  ConfigService,
  ApiConfigService,
  type Tuple,
} from '@nextgen-web-framework/core';
import {
  PaymentStatus,
  CTAs,
  ChannelSpecificCheckCTA,
  HYBIRS_ORDER_STATUS,
  ICancelCodeCMS,
  IFulfillmentTypeCMSData,
  ORDER_DELIVERY_TYPES,
  OTHER_REASON_IDS,
  OrderStatus,
  SOURCE_OMS,
  CREDIT_DEBIT_CARD,
  INSTALLMENT_PAYMENT,
  PAYMENT_STATUS,
  HYBRIS_PAYMENT_STATUS,
} from '../types';
import { Entity, OrderLine } from '../services';
import type {
  IBundleProduct,
  IHTMLWithDynamicData,
  IOrderLine,
  IProductWithImageInterface,
  ProductCardTagProps,
  CreditNoteDocumentList,
  CancellationDetailsTypes,
} from '../types/postpurchaseTypes';
import { Cancellation } from '../services/models/Cancellation';
import { IReturnBundle } from '../types/returnTypes';
export function isBundleProduct(product?: OrderLine) {
  return product?.bundleProducts && product?.bundleProducts?.length > 0;
}

export function isSopProduct(product?: OrderLine) {
  return product?.subscriptionProducts && product?.subscriptionProducts?.length > 0;
}

export function isSopProductAvailable(product?: OrderLine) {
  if (product?.bundleProducts && isBundleProduct(product)) {
    for (const item of product.bundleProducts) {
      if (item?.subscriptionElementId !== undefined) {
        return true;
      }
    }
  }
  return product?.subscriptionElementId !== undefined;
}

function getProductPrice(product: IOrderLine, isBundle?: boolean, isReturn?: boolean) {
  if (isReturn) {
    return isBundle ? product.calculatedBundleOrderLineTotal : product.orderLineTotal;
  }
  return isBundle ? product.calculatedBundleOriginalLineTotalValue : product.originalLineTotalValue;
}

function getItemPrice(product: IOrderLine, isBundle?: boolean) {
  return isBundle ? product.calculatedBundlePrice : product.linePrice;
}

function getProductPV(product: IOrderLine, isBundle?: boolean, isReturn?: boolean) {
  if (isReturn) {
    return (isBundle ? product?.calculatedBundleLinePVAmount : product?.linePVAmount)?.toString();
  }

  return (isBundle ? product?.calculatedBundleOriginalLinePVAmount : product?.originalLinePVAmount)?.toString();
}

function getProductBV(product: IOrderLine, isBundle?: boolean, isReturn?: boolean) {
  if (isReturn) {
    return (isBundle ? product?.calculatedBundleLineBVAmount : product?.lineBVAmount)?.toString();
  }

  return (isBundle ? product?.calculatedBundleOriginalLineBVAmount : product?.originalLineBVAmount)?.toString();
}

export function productBasicData(product: IOrderLine, currencyCode?: string) {
  return {
    brandName: product?.itemBrand,
    productName: product.itemDescription || '',
    productSize: product.itemSize || '',
    servingSize: product.itemSize || '',
    alt: product?.media?.main?.altText,
    link: product?.media?.main?.publicUrl,
    thumbnail: product?.media?.main?.publicUrl,
    main: product?.media?.main?.publicUrl ?? product?.media?.zoom?.publicUrl,
    productId: product?.baseItemId,
    stockStatus: '',
    currency: currencyCode,
    orderLineId: product.orderLineId,
    showLinkForProductCard: !product?.restrictFromPdpDisplay,
  };
}

export function getProductInformation(product: IOrderLine, currencyCode: string, isBundle?: boolean) {
  const inventoryReservationLineId = isBundle
    ? product?.bundleProducts?.[0]?.inventoryReservationLineId
    : product?.inventoryReservationLineId;
  return {
    ...productBasicData(product, currencyCode),
    inventoryReservationLineId:
      inventoryReservationLineId ||
      product?.subscriptionProducts?.[0]?.bundleProducts?.[0]?.inventoryReservationLineId ||
      product?.subscriptionProducts?.[0]?.inventoryReservationLineId,
    restrictFromReturns: product?.restrictFromReturns,
    strikethroughPrice: product?.calculatedStrikeOutPrice,
    calculatedBundleReturnableQuantity: product?.calculatedBundleReturnableQuantity,
    cancelledQuantity: product?.cancelledQuantity,
    deliveryDate: dateTimeFormatter(
      product?.actualDeliveryDate,
      DateFormats.MEDIUM_DATE,
      undefined,
      DateOrTime.DATE_ONLY,
    ),
    dateSubText: dateTimeFormatter(
      product?.estimatedShipDate,
      DateFormats.MEDIUM_DATE,
      undefined,
      DateOrTime.DATE_ONLY,
    ),
    subscriptionProduct: product.subscriptionProducts,
  };
}

export function getTagBasedOnCheck(tagCheck: boolean | undefined | 0, tag: ProductCardTagProps) {
  if (tagCheck) {
    return [tag];
  }
  return [];
}

export function isOrderStatusVisible(showOrderStatusFlag: boolean, isCancelledOrder?: boolean, paymentStatus?: string) {
  if (showOrderStatusFlag) {
    return isCancelledOrder
      ? true
      : ![
          ...PAYMENT_STATUS.AWAITED.keys,
          ...PAYMENT_STATUS.PENDING.keys,
          ...PAYMENT_STATUS.DENIED.keys,
          ...HYBRIS_PAYMENT_STATUS.NOTPAID.keys,
        ].includes(paymentStatus || '');
  }

  return true;
}

function checkLineReturnEnable(product?: IOrderLine) {
  if (product?.calculatedIsReturnEnabled === false) {
    return false;
  }
  if (product?.calculatedIsReturnEnabled) {
    return product?.restrictFromReturns === true ? false : !product?.restrictFromReturnsOnline;
  }
  if (product?.calculatedIsReplacementEnabled === false) {
    return false;
  }
  if (product?.calculatedIsReplacementEnabled) {
    return product?.isDeliveryAvailable && ['IN_STOCK', 'LOW_STOCK'].includes(product?.derivedAvailabilityStatus || '');
  }
  return false;
}

export function getProductInfo(
  product: IOrderLine,
  currencyCode: string,
  isReturn?: boolean,
  isBundle?: boolean | undefined,
  isTracking?: boolean,
  isReplacementOrder?: boolean,
  useNewMapping?: boolean,
) {
  const productData = getProductInformation(product, currencyCode, isBundle);
  return {
    ...productData,
    productName: product?.subscriptionPlanName || product.itemDescription || '',
    productSize: product?.subscriptionCode ? `Subscription ID: ${product.subscriptionCode}` : product.itemSize || '',
    itemPrice: getItemPrice(product, isBundle),
    totalTax: product?.lineTaxTotalAmount,
    productPrice: product?.calculatedSubscriptionOriginalLineTotalValue || getProductPrice(product, isBundle, isReturn),
    showZeroAsFree: !isReplacementOrder,
    productQuantity: isTracking
      ? useNewMapping
        ? product?.fulfillments?.[0]?.calculatedGroupedFulfilledQuantity
        : product?.fulfillments?.[0]?.fulfilledQuantity
      : getProductQuantity(product, isReturn, isBundle),
    isTNAProduct: Number(product?.unallocatedQuantity) > 0,
    derivedAvailabilityStatus: product?.qtyInitiallyBackordered && !isReturn ? 'BO' : '',
    isCancelled: product.isCancelled,
    businessVolume:
      product?.calculatedSubscriptionOriginalLineBVAmount?.toString() || getProductBV(product, isBundle, isReturn),
    pointValue:
      product?.calculatedSubscriptionOriginalLinePVAmount?.toString() || getProductPV(product, isBundle, isReturn),
    isReturnEnabled: isBundle ? product.calculatedIsBundleReturnEnabled : checkLineReturnEnable(product),
    tags: [
      ...getTagBasedOnCheck(product?.calculatedIsPromotionalItem && !product.isAuxiliaryProduct, {
        icon: {
          iconName: 'tags',
          iconStyle: IconStyle.REGULAR,
        },
        text: '',
        style: TagStyle.SOLID,
        theme: Theme.SUCCESS,
      }),
      ...getTagBasedOnCheck(product?.subscriptionElementId !== undefined, {
        icon: {
          iconName: 'box',
          iconStyle: IconStyle.REGULAR,
        },
        text:
          product?.subscriptionElementSequence && product?.subscriptionTotalShipments
            ? `SOP: ${product?.subscriptionElementSequence}/${product?.subscriptionTotalShipments}`
            : 'SOP',
        style: TagStyle.OUTLINE,
        theme: Theme.PURPLE,
      }),
    ],
    subscriptionID: product?.subscriptionID,
    subscriptionElementId: product?.subscriptionElementId,
    isBundleProduct: isBundle,
    isSubscriptionCompensated: product?.isSubscriptionCompensated,
    busLineCode: product?.busLineCodeEbs,
    uom: product?.lineUnitOfMeasure,
  } as IProductWithImageInterface;
}

export function getProductQuantity(product: IOrderLine, isReturn?: boolean, isBundle?: boolean) {
  if (isReturn) {
    return isBundle ? product.calculatedBundleReturnableQuantity : product.returnableQuantity;
  }
  return isBundle ? product.bundleQuantity : product.orderedQuantity;
}

export function checkCancellationCreditNote(
  product: OrderLine,
  creditNoteDocumentUrlList?: Array<CreditNoteDocumentList>,
) {
  const cancelDetails: Array<CancellationDetailsTypes> = getOrderCancellationDetails([{ ...product }]);
  cancelDetails.forEach((cancelObj) => {
    if (
      cancelObj?.cancelDocumentURL &&
      creditNoteDocumentUrlList &&
      creditNoteDocumentUrlList.findIndex((item) => cancelObj?.cancelDocumentURL === item.url) === -1
    ) {
      creditNoteDocumentUrlList.push({
        dateTime: cancelObj?.cancelDate,
        url: cancelObj?.cancelDocumentURL,
      });
    }
  });
}

export function checkDerivedAvailablityStatus(
  product: IOrderLine,
  isCancelledOrder?: boolean,
  isReplacement?: boolean,
) {
  if (isReplacement) {
    return product?.derivedAvailabilityStatus;
  }
  if (product?.cancelledQuantity && product?.cancelledQuantity > 0 && !isCancelledOrder) {
    return 'CANCELLED';
  } else if (product?.qtyInitiallyBackordered) {
    return 'BO';
  }
  return '';
}

export function createBundleData(
  product: IOrderLine,
  isReturnEligibility?: boolean,
  creditNoteDocumentUrlList?: Array<CreditNoteDocumentList>,
  isCancelledOrder?: boolean,
  isReplacement?: boolean,
  setLatamPayload?: boolean,
): {
  bundleData: Array<IBundleProduct & IReturnBundle>;
  hasCancelledItemsInBundle: boolean;
  noItemEligible: boolean;
  totalTaxAmount?: number;
  totalItemPrice?: number;
} {
  const data: Array<IBundleProduct & IReturnBundle> = [];
  let hasCancelledItems = false;
  let totalTaxAmount = 0;
  let totalItemPrice = 0;
  let noItemEligible = true;
  product.bundleProducts?.forEach((bundle: IOrderLine) => {
    checkCancellationCreditNote(bundle, creditNoteDocumentUrlList);
    if (bundle?.cancelledQuantity && bundle.cancelledQuantity > 0) {
      hasCancelledItems = true;
    }
    if (bundle?.lineTaxTotalAmount) {
      totalTaxAmount += bundle?.lineTaxTotalAmount;
    }
    if (bundle?.linePrice) {
      totalItemPrice += bundle?.linePrice;
    }
    if (bundle?.calculatedIsReturnEnabled) {
      noItemEligible = false;
    }
    data.push({
      productId: bundle.baseItemId || '',
      brandName: bundle.itemBrand || '',
      productName: bundle.itemDescription || '',
      alt: bundle.media?.main?.altText || '',
      link: bundle.media?.main?.publicUrl || '',
      quantity: bundle?.returnOrderlineQuantity
        ? bundle.returnOrderlineQuantity
        : isReturnEligibility && bundle.returnableQuantity !== undefined && !setLatamPayload
          ? bundle.returnableQuantity
          : isReplacement && bundle?.replacementOrderlineQuantity !== undefined
            ? bundle?.replacementOrderlineQuantity
            : bundle.orderedQuantity || 0,
      servingSize: bundle.itemSize || '',
      uom: bundle.lineUnitOfMeasure,
      busLineCode: bundle.busLineCodeEbs,
      parentOrderLineId: bundle?.parentOrderLineId || bundle?.inventoryReservationLineId,
      baseitemId: bundle.baseItemId,
      selectedReturnQuantity: isReplacement ? bundle.replacementBundleQuantity : bundle.returnOrderlineQuantity,
      returnableQuantity: bundle.returnableQuantity,
      bundleItemId: bundle?.bundleItemId,
      bundleLineId: bundle?.bundleLineId,
      subscriptionID: bundle?.subscriptionID,
      subscriptionElementId: bundle?.subscriptionElementId,
      returnItemUserImageUrl: bundle?.returnItemUserImageUrl,
      returnReason: bundle?.returnReason,
      returnComment: bundle?.returnComment,
      parentOrderLineQuantity: bundle?.orderedQuantity,
      parentOrderBundleQuantity: bundle?.bundleQuantity,
      lineBVAmount: bundle?.lineBVAmount,
      linePVAmount: bundle?.linePVAmount,
      orderLineTotal: bundle?.orderLineTotal,
      returnLineId: bundle?.returnLineId,
      inventoryReservationLineId: bundle?.inventoryReservationLineId,
      cancelledQuantity: bundle?.cancelledQuantity,
      derivedAvailablityStatus: checkDerivedAvailablityStatus(bundle, isCancelledOrder, isReplacement),
      firstAvailableFutureDate: bundle?.estimatedShipDate,
      itemPrice: bundle?.linePrice,
      totalTax: bundle?.lineTaxTotalAmount,
      productPrice: bundle?.originalLineTotalValue,
      isReturnEnabled: checkLineReturnEnable(bundle),
      returnedQty: 1,
      isPromotionApplied:
        !product?.calculatedIsPromotionalItem && bundle?.calculatedIsPromotionalItem && !bundle.isAuxiliaryProduct,
    });
  });
  return {
    bundleData: data,
    hasCancelledItemsInBundle: hasCancelledItems,
    totalTaxAmount,
    totalItemPrice,
    noItemEligible,
  };
}

export function createBundleReorderData(product: OrderLine) {
  const bundleData: BundleSlot[] = [];
  product?.bundleProducts?.forEach((bundleItem, index) => {
    bundleData.push({
      id: `${index}`,
      entries: [
        {
          product: {
            id: bundleItem?.baseItemId || '',
          },
          quantity: bundleItem.orderedQuantity || 0,
        },
      ],
    });
  });
  return bundleData;
}

export function generateDateStr(date: Date) {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getOrderDeliveryType(deliveryType: string) {
  let orderDeliveryType = deliveryType ? deliveryType.toLowerCase() : 'homedelivery';
  for (const key in ORDER_DELIVERY_TYPES) {
    if (ORDER_DELIVERY_TYPES[key].indexOf(deliveryType.toLowerCase()) >= 0) {
      orderDeliveryType = key;
    }
  }
  return orderDeliveryType;
}

export function getFulfillmentType(order?: Entity) {
  const fulfillemtType = isBundleProduct(order?.orderLines?.[0])
    ? order?.orderLines?.[0]?.bundleProducts?.[0]?.fulfillmentType
    : order?.orderLines?.[0]?.fulfillmentType;
  return fulfillemtType?.toLowerCase() || '';
}

export function getCalculatedOrderStatus(order?: Entity) {
  if (SOURCE_OMS.HYBRIS === order?.sourceOms) {
    if (order.orderStatus && HYBIRS_ORDER_STATUS[order.orderStatus]) {
      return HYBIRS_ORDER_STATUS[order.orderStatus];
    }
    return order.orderStatus;
  }
  return order?.calculatedOrderStatus;
}

export function getHtmlStringWithDynamicData({ htmlString, dynamicData }: IHTMLWithDynamicData) {
  let htmlstr = '';
  if (htmlString) {
    htmlstr = htmlString.replace(/\{(\w+)\}/g, (match, key) =>
      dynamicData[key] !== undefined ? dynamicData[key] : match,
    );
  }
  return htmlstr;
}

export function getPhoneCodeByCountryCode(countryCode = '') {
  let countrydata: CountryInterface | undefined;
  if (countryCode) {
    countrydata = MasterCountryList?.find((country: CountryInterface) => country.ISO2 === countryCode);
  }
  return countrydata?.phoneCode;
}

export function isOrderCancelled(calculatedOrderStatus: string | undefined) {
  return OrderStatus.CANCELLED.includes(calculatedOrderStatus || '');
}

export function getCancellations(cancellations?: Cancellation[], cancelCodesCMS?: Array<ICancelCodeCMS>) {
  const cancellationData: Array<{}> = [];
  cancellations?.forEach((cancelData) => {
    const cancelObj = cancelCodesCMS?.find((ob) => ob.reason_code === cancelData?.cancelReason);
    cancellationData.push({
      cancelDate: dateTimeFormatter(
        cancelData?.cancelDateTime,
        DateFormats.MEDIUM_DATE,
        undefined,
        DateOrTime.DATE_ONLY,
      ),
      cancelReason:
        cancelObj?.reason_code && OTHER_REASON_IDS.includes(cancelObj?.reason_code)
          ? cancelData?.note
          : cancelObj?.reason_description || cancellations?.[0]?.cancelReason,
      cancelTime: dateTimeFormatter(
        cancelData?.cancelDateTime,
        undefined,
        TimeFormats.SHORT_TIME,
        DateOrTime.TIME_ONLY,
      ),
      cancelDocumentURL: cancelData?.cancelDocumentURL,
      cancelledByUser: cancelData?.cancelledByUser,
      cancelComments: cancelData?.note,
    });
  });
  return cancellationData;
}

export function getOrderCancellationDetails(orderLines?: OrderLine[], cancelCodesCMS?: Array<ICancelCodeCMS>) {
  let cancellationDetails: Array<{}> = [];
  if (orderLines && isBundleProduct(orderLines?.[0])) {
    cancellationDetails = getOrderCancellationDetails(orderLines?.[0]?.bundleProducts, cancelCodesCMS);
  } else if (orderLines && isSopProduct(orderLines?.[0])) {
    cancellationDetails = getOrderCancellationDetails(orderLines?.[0]?.subscriptionProducts, cancelCodesCMS);
  } else {
    cancellationDetails = getCancellations(orderLines?.[0]?.cancellations, cancelCodesCMS);
  }
  return cancellationDetails;
}

export const channelSpecificCheckODCTA = (CTA: CTAs, sourceOms?: string) => {
  const channel = ChannelSpecificCheckCTA.find((item) => item.sourceOms === sourceOms);
  if (channel) {
    return channel.OrderDetailsCTAs.includes(CTA);
  }
  return true;
};

export const channelSpecificCheckOHCTA = (CTA: CTAs, flag?: boolean, sourceOms?: string) => {
  const channel = ChannelSpecificCheckCTA.find((item) => item.sourceOms === sourceOms);
  if (flag && channel) {
    return channel.OrderHistoryCTAs.includes(CTA);
  }
  return flag;
};

export const channelSpecificCheckODCTAHandling = (CTA: CTAs, flag?: boolean, sourceOms?: string) => {
  const channel = ChannelSpecificCheckCTA.find((item) => item.sourceOms === sourceOms);
  if (flag && channel) {
    return channel.OrderDetailsCTAs.includes(CTA);
  }
  return flag;
};

export function dateTimeFormatter(
  date?: string,
  dateFormat?: DateFormats,
  timeFormat?: TimeFormats,
  dateOrTime?: DateOrTime,
): string {
  const configs = ConfigService.getConfigs();

  if (!date) {
    return '';
  }
  const { locale } = PreferenceService.getPreferences();
  let calculatedDateFormat: DateFormats | undefined;
  if (locale === 'es-co') {
    calculatedDateFormat = DateFormats.LONG_DATE;
  } else {
    calculatedDateFormat = dateFormat;
  }
  return formatDateTime(
    date,
    { date: calculatedDateFormat, time: timeFormat },
    false,
    configs.NEXT_PUBLIC_TIMEZONE as string,
    locale || undefined,
    dateOrTime,
  );
}

export const getStoreImageUrl = (fulfillmentTypeCMSData?: IFulfillmentTypeCMSData[], deliveryOption?: string) =>
  fulfillmentTypeCMSData?.[0]?.types?.find(
    (item) => item.fulfillment_type_id?.toLocaleLowerCase() === deliveryOption?.toLocaleLowerCase(),
  )?.image?.url;

interface LoginState {
  headers: Tuple;
  redirectUri: string;
  loginUrl?: string;
  amwayLang?: string;
}

export const getLoginProps = (currentLocale: string) => {
  const tuples = ApiConfigService.getTuples();
  const configs = ConfigService.getConfigs();
  let postAuthUrl = '';
  if (typeof window !== 'undefined') {
    const loginState: LoginState = {
      redirectUri: window.location.href,
      headers: tuples,
      amwayLang: currentLocale,
    };
    if (configs.NEXT_PUBLIC_LOGIN_URL) {
      loginState.loginUrl = configs.NEXT_PUBLIC_LOGIN_URL as string;
    }
    postAuthUrl = btoa(JSON.stringify(loginState));
  }
  return {
    scope: 'openid',
    state: postAuthUrl,
    countryIso: tuples['X-Amway-Country'],
    redirectUri: configs.NEXT_PUBLIC_OIDC_URL ? `${configs.NEXT_PUBLIC_OIDC_URL}` : '',
  };
};

export const getBusinessNature = (loggedInAccountId: string, order?: Entity) => {
  if (loggedInAccountId === order?.orderingSalesAccount?.accountId) {
    return order?.orderingSalesAccount?.businessNature;
  } else if (loggedInAccountId === order?.orderForSalesAccount?.accountId) {
    return order?.orderForSalesAccount?.businessNature;
  }
  return undefined;
};

export const isABO = (businessNature?: string) => AccountType.BusinessOwner === businessNature;

export const isMember = (businessNature?: string) => AccountType.Member === businessNature;

export const SOPLabel = 'SOP';

export const getImageUrl = (path: string): string => {
  const configs = ConfigService.getConfigs();
  return path ? `${configs.NEXT_ASSETS_BASE_PATH}/${path}` : '';
};

export const checkRetryPopupStatus = (paymentInfo: PaymentDetails) => {
  let isRetryButtonEnable = false;
  paymentInfo.transactions.forEach((transaction) => {
    if (
      !isRetryButtonEnable &&
      (transaction?.paymentMethodCode === CREDIT_DEBIT_CARD ||
        transaction?.paymentMethodCode === INSTALLMENT_PAYMENT) &&
      transaction?.status === PaymentStatus.INITIATED
    ) {
      isRetryButtonEnable = true;
    }
  });
  return isRetryButtonEnable;
};

export function deepEqualCheck(firstValue: DynamicObjectInterface, secondValue: DynamicObjectInterface): boolean {
  if (firstValue === secondValue) {
    return true;
  }

  if (
    firstValue === null ||
    secondValue === null ||
    typeof firstValue !== 'object' ||
    typeof secondValue !== 'object'
  ) {
    return false;
  }

  if (Array.isArray(firstValue) !== Array.isArray(secondValue)) {
    return false;
  }

  const firstKeys = Object.keys(firstValue);
  const secondKeys = Object.keys(secondValue);

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  for (const key of firstKeys) {
    if (!secondKeys.includes(key)) {
      return false;
    }
    if (!deepEqualCheck(firstValue[key], secondValue[key])) {
      return false;
    }
  }

  return true;
}
